import { useEffect, useState } from 'react';
import type { DeliveryExplanation, DeliveryIssueExplanation } from './buildApi';

/** The same evidence explains the customer impact and the PI team's next step. */
export function DeliveryMeaning({ explanation }: { explanation: DeliveryExplanation }): JSX.Element {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  useEffect(() => setCopyState('idle'), [explanation.customerMessage]);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(explanation.customerMessage);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };
  return (
    <>
      <div className="bl-meaning-grid">
        <div>
          <h3>What this means</h3>
          <p>{explanation.summary}</p>
        </div>
        <div>
          <h3>How we know</h3>
          <p>{explanation.evidence}</p>
        </div>
        <div>
          <h3>Why it matters</h3>
          <p>{explanation.customerImpact}</p>
          <p><strong>PI next step:</strong> {explanation.piAction}</p>
        </div>
      </div>
      <details className="bl-report-disclosure bl-customer-update">
        <summary>Customer update</summary>
        <p>{explanation.customerMessage}</p>
        <button type="button" className="bl-btn bl-btn--secondary bl-btn--small" onClick={() => void copy()}>
          {copyState === 'copied' ? 'Copied' : 'Copy customer update'}
        </button>
        {copyState === 'failed' && <span className="bl-hint" role="status"> Select the text above and copy it.</span>}
      </details>
    </>
  );
}

export function IssueMeaning({ issue }: { issue: DeliveryIssueExplanation }): JSX.Element {
  return (
    <article className={`bl-meaning-issue bl-meaning-issue--${issue.kind}`}>
      <h4>{issue.summary}</h4>
      <p><strong>How we know:</strong> {issue.evidence}</p>
      <p><strong>Customer impact:</strong> {issue.customerImpact}</p>
      <p><strong>PI next step:</strong> {issue.piAction}</p>
      <details className="bl-details">
        <summary>Technical reference</summary>
        <div className="bl-details-body bl-mono">{issue.stack} · {issue.code}</div>
      </details>
    </article>
  );
}

export function AttentionMeaning({ explanation, code }: { explanation?: DeliveryIssueExplanation; code: string }): JSX.Element {
  return (
    <div className="bl-issue-summary">
      <strong>{explanation?.summary ?? 'This item needs investigation'}</strong>
      <p>{explanation?.customerImpact ?? 'The affected build step has not completed.'}</p>
      <p><strong>PI next step:</strong> {explanation?.piAction ?? 'Inspect the affected item and saved failure before retrying.'}</p>
      <details className="bl-details">
        <summary>Evidence and technical reference</summary>
        <div className="bl-details-body">
          {explanation && <p>{explanation.evidence}</p>}
          <code>{code}</code>
        </div>
      </details>
    </div>
  );
}
